import { useRef, useEffect, useCallback, useState, useMemo } from 'react';
import logger from '@/utils/logger';

export interface AudioSystemConfig {
  volumeUpdateInterval?: number;
  smoothingTimeConstant?: number;
  fftSize?: number;
}

export interface AudioSystemState {
  currentVolume: number;
  isMonitoring: boolean;
  isSupported: boolean;
  error: Error | null;
}

export interface AudioSystemControls {
  startVolumeMonitoring: () => Promise<void>;
  stopVolumeMonitoring: () => void;
  getCurrentVolume: () => number;
  isMonitoringActive: () => boolean;
}

const DEFAULT_CONFIG: Required<AudioSystemConfig> = {
  volumeUpdateInterval: 50,
  smoothingTimeConstant: 0.3,
  fftSize: 256,
};

/**
 * 音频系统管理 Hook
 * 恢复到原始的独立音频流获取和音量检测实现
 */
export function useAudioSystem(
  onVolumeChange?: (volume: number) => void,
  config: AudioSystemConfig = {}
): AudioSystemState & AudioSystemControls {
  const finalConfig = useMemo(() => ({ ...DEFAULT_CONFIG, ...config }), [config]);

  // 音频相关引用
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const rafRef = useRef<number | null>(null);
  const isMountedRef = useRef(true);

  // 状态管理
  const [state, setState] = useState<AudioSystemState>({
    currentVolume: 0,
    isMonitoring: false,
    isSupported: typeof window !== 'undefined' && !!navigator.mediaDevices?.getUserMedia,
    error: null,
  });

  // 启动音量监控 - 使用原始实现
  const startVolumeMonitoring = useCallback(async (): Promise<void> => {
    if (state.isMonitoring || !state.isSupported) return;
    // 幂等保护：若资源已存在，直接返回
    if (audioContextRef.current || streamRef.current || analyserRef.current) {
      setState((prev) => ({ ...prev, isMonitoring: true }));
      return;
    }

    try {
      setState((prev) => ({ ...prev, error: null }));
      logger.debug('[AudioSystem] 初始化音量监测系统');

      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      if (audioContext.state === 'suspended') {
        await audioContext.resume();
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
        },
      });

      const analyser = audioContext.createAnalyser();
      analyser.fftSize = finalConfig.fftSize;
      analyser.smoothingTimeConstant = finalConfig.smoothingTimeConstant;

      const microphone = audioContext.createMediaStreamSource(stream);
      microphone.connect(analyser);

      audioContextRef.current = audioContext;
      analyserRef.current = analyser;
      streamRef.current = stream;

      setState((prev) => ({ ...prev, isMonitoring: true }));

      // 优化的音量检测循环
      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);
      let lastUpdateTime = 0;
      const updateInterval = finalConfig.volumeUpdateInterval;

      const detectVolume = (timestamp: number) => {
        if (!analyserRef.current || !isMountedRef.current) {
          return;
        }

        // 限制更新频率，避免过度计算
        if (timestamp - lastUpdateTime < updateInterval) {
          rafRef.current = requestAnimationFrame(detectVolume);
          return;
        }
        lastUpdateTime = timestamp;

        analyserRef.current.getByteFrequencyData(dataArray);

        // 优化音量计算 - 只计算前1/4的频率数据（通常包含主要的音频信息）
        let sum = 0;
        const sampleSize = Math.floor(bufferLength / 4);
        for (let i = 0; i < sampleSize; i++) {
          sum += dataArray[i] * dataArray[i];
        }
        const volume = Math.sqrt(sum / sampleSize) / 255;

        setState((prev) => ({ ...prev, currentVolume: volume }));
        onVolumeChange?.(volume);

        if (isMountedRef.current) {
          rafRef.current = requestAnimationFrame(detectVolume);
        }
      };

      rafRef.current = requestAnimationFrame(detectVolume);

      logger.debug('[AudioSystem] 音量监测系统初始化完成');
    } catch (error) {
      const err = error as Error;
      logger.warn('[AudioSystem] 音量监测系统初始化失败:', err);
      setState((prev) => ({ ...prev, error: err }));
    }
  }, [state.isMonitoring, state.isSupported, finalConfig, onVolumeChange]);

  // 停止音量监控
  const stopVolumeMonitoring = useCallback((): void => {
    logger.debug('[AudioSystem] 停止音量监控');
    isMountedRef.current = false;

    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => {
        track.stop();
        logger.debug('[AudioSystem] 音频轨道已停止:', track.kind);
      });
      streamRef.current = null;
    }

    if (audioContextRef.current) {
      try {
        if (audioContextRef.current.state !== 'closed') {
          audioContextRef.current.close().catch((e) => {
            logger.warn('[AudioSystem] 关闭音频上下文失败:', e);
          });
        }
      } catch (e) {
        logger.warn('[AudioSystem] 关闭音频上下文异常:', e);
      }
      audioContextRef.current = null;
    }

    analyserRef.current = null;

    setState((prev) => ({
      ...prev,
      isMonitoring: false,
      currentVolume: 0,
    }));

    onVolumeChange?.(0);
  }, [onVolumeChange]);

  // 组件卸载时清理资源
  useEffect(() => {
    return () => {
      stopVolumeMonitoring();
    };
  }, [stopVolumeMonitoring]);

  const getCurrentVolume = useCallback((): number => {
    return state.currentVolume;
  }, [state.currentVolume]);

  const isMonitoringActive = useCallback((): boolean => {
    return state.isMonitoring;
  }, [state.isMonitoring]);

  return useMemo(
    () => ({
      // 状态
      currentVolume: state.currentVolume,
      isMonitoring: state.isMonitoring,
      isSupported: state.isSupported,
      error: state.error,

      // 控制方法
      startVolumeMonitoring,
      stopVolumeMonitoring,
      getCurrentVolume,
      isMonitoringActive,
    }),
    [
      state.currentVolume,
      state.isMonitoring,
      state.isSupported,
      state.error,
      startVolumeMonitoring,
      stopVolumeMonitoring,
      getCurrentVolume,
      isMonitoringActive,
    ]
  );
}
